import clsx from 'clsx';
import { FC, InputHTMLAttributes, forwardRef, useCallback, useState } from 'react';
import { IMaskInput } from 'react-imask';

import { getInputAria } from './Input.helpers';
import { InputBase } from './Input.type';
import { FieldWrapper } from './atoms/FieldWrapper';

type TimeInputMode = 'date' | 'month';

type Props = {
  variant: TimeInputMode;
  minYear?: number;
  maxYear?: number;
} & Prettify<
  InputBase<string> &
    Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> & {
      onChange: (value: string) => void;
    }
>;

const convertToISOFormat = (displayValue: string, variant: TimeInputMode): string => {
  if (variant === 'date') {
    // Convert DD/MM/YYYY to YYYY-MM-DD
    const parts = displayValue.split('/');
    if (parts.length === 3) {
      const [day, month, year] = parts;
      return `${year}-${month}-${day}`;
    }
  } else {
    // Convert MM/YYYY to YYYY-MM
    const parts = displayValue.split('/');
    if (parts.length === 2) {
      const [month, year] = parts;
      return `${year}-${month}`;
    }
  }

  return '';
};

const convertFromIsoToDisplayFormat = (isoValue: string, variant: TimeInputMode): string => {
  if (!isoValue || typeof isoValue !== 'string') {
    return '';
  }
  if (variant === 'date') {
    // Convert YYYY-MM-DD to DD/MM/YYYY
    const parts = isoValue.split('-');
    if (parts.length === 3 && parts[0].length === 4 && parts[1].length === 2 && parts[2].length === 2) {
      const [year, month, day] = parts;
      return `${day}/${month}/${year}`;
    }
  } else {
    // Convert YYYY-MM to MM/YYYY
    const parts = isoValue.split('-');
    if (parts.length === 2 && parts[0].length === 4 && parts[1].length === 2) {
      const [year, month] = parts;
      return `${month}/${year}`;
    }
  }

  return '';
};

export const InputTime: FC<Props> = forwardRef<HTMLInputElement, Props>(
  (
    { className, inputClassName, variant, onChange, label, placeholder, labelOutside, minYear, maxYear, ...props },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const handleAccept = useCallback(
      (acceptedValue: string) => {
        const newISOValue = convertToISOFormat(acceptedValue, variant);
        if (newISOValue !== props.value) {
          onChange(newISOValue);
        }
      },
      [onChange, variant, props.value],
    );
    const displayValue = convertFromIsoToDisplayFormat(props.value ?? '', variant);
    return (
      <FieldWrapper {...props} className={className} label={label} labelOutside={labelOutside}>
        <IMaskInput
          ref={ref}
          {...(props as any)}
          {...getInputAria(props)}
          type="text"
          value={displayValue}
          onAccept={handleAccept}
          onFocus={(e) => {
            setIsFocused(true);
            props.onFocus?.(e);
          }}
          onBlur={(e) => {
            setIsFocused(false);
            props.onBlur?.(e);
          }}
          mask={variant === 'date' ? '00/00/0000' : '00/0000'}
          autofix="false"
          lazy={!isFocused}
          placeholderChar="_"
          inputMode="numeric"
          className={clsx(inputClassName, 'font-family-default tracking-wider tabular-nums')}
          placeholder={placeholder ?? (variant === 'date' ? 'DD/MM/YYYY' : 'MM/YYYY')}
        />
      </FieldWrapper>
    );
  },
);

InputTime.displayName = 'InputTime';
